/**
 * Formato de fechas y números para la consola.
 *
 * 🚨 Existe por un bug real: Postgres devuelve los timestamp como
 * `2026-08-20 03:46:42.854123-05` — con el offset de zona en DOS dígitos.
 * `new Date()` exige `-05:00`, con minutos, así que reventaba con
 * `RangeError: Invalid time value` y tumbaba la pantalla entera.
 *
 * El typecheck no lo veía (los dos son `string`) y las pruebas del backend
 * tampoco: solo apareció al abrir la página en el navegador.
 */

/**
 * Convierte un timestamp de Postgres a Date, o null si no se puede.
 *
 * Devuelve null en vez de un `Invalid Date` a propósito: un Invalid Date se
 * propaga silencioso hasta que algo lo formatea y revienta la interfaz. Un null
 * obliga a decidir qué mostrar en el lugar donde se sabe qué mostrar.
 */
export function comoFecha(valor: string | null | undefined): Date | null {
  if (!valor) return null

  let iso = valor.trim().replace(' ', 'T')

  // El arreglo: `-05` → `-05:00`. Solo si el offset viene sin minutos.
  iso = iso.replace(/([+-]\d{2})$/, '$1:00')

  const fecha = new Date(iso)
  return Number.isNaN(fecha.getTime()) ? null : fecha
}

const HORA = new Intl.DateTimeFormat('es-CO', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

/**
 * `Intl` en es-CO produce «20 de ago de 26» — tres palabras de relleno en una
 * columna de tabla. Se arma a mano para que quepa: «20 ago 26».
 */
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

function fechaCompacta(f: Date): string {
  const dia = String(f.getDate()).padStart(2, '0')
  const mes = MESES[f.getMonth()] ?? '???'
  const anio = String(f.getFullYear()).slice(2)
  return `${dia} ${mes} ${anio}`
}

const NUMERO = new Intl.NumberFormat('es-CO')

export function hora(valor: string | null | undefined): string {
  const f = comoFecha(valor)
  return f ? HORA.format(f) : '—'
}

export function fechaCorta(valor: string | null | undefined): string {
  const f = comoFecha(valor)
  return f ? fechaCompacta(f) : '—'
}

export function fmt(valor: string | number | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '—'
  const n = Number(valor)
  return Number.isFinite(n) ? NUMERO.format(n) : String(valor)
}

/** Cédula con separadores: 1.124.406.339 se lee; 1124406339 no. */
export function cedula(valor: string): string {
  const n = Number(valor)
  return Number.isFinite(n) ? NUMERO.format(n) : valor
}
